import { PaperclipIcon, SendHorizontalIcon, XIcon } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ATTACHMENT_ABOUT_KEY } from "@/features/messages/attachment-copy"
import { useGlobalSearch, MIN_SEARCH_LENGTH } from "@/features/search/use-global-search"
import { useIsOnline, useSendMessage } from "@/features/messages/use-messages"
import { ApiError } from "@/lib/api-error"
import { COMPOSER_BOX_CLASS, composerRows } from "@/lib/composer-size"
import {
  MESSAGE_BODY_MAX,
  type MessageAttachmentInput,
  type MessageAttachmentType,
} from "@/lib/endpoints"
import { cn } from "@/lib/utils"

/**
 * The compose box.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS ABSENT — NOT DISABLED — WITHOUT `message.send`.
 *
 * A greyed-out box invites the reader to hunt for the way to un-grey it, and for
 * a capability their role does not hold there is no such way. The screen renders
 * a one-line explanation in its place instead. This is the same rule the
 * quotation "Edit" link and the challan editor follow (`routes/paths.ts`).
 *
 * The component itself is only mounted when the capability is held (see
 * `GroupRoom`), so there is no branch here that could be got wrong.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * OFFLINE, IT DISABLES AND SAYS SOMETHING TRUE.
 *
 * The app has Dexie offline drafts, so the obvious sentence is "messages send
 * when you are back online". THAT WOULD BE A LIE: queueing means ordering,
 * dedupe on retry and the same idempotent-UUID discipline the document sync
 * layer carries, and none of it is built. v1 is online-only, so the box turns
 * off and says the message has not been sent and to try again when there is
 * signal. A reader who believes a false promise stops watching for the reply.
 */

/**
 * What the box needs from whoever actually posts the message.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE RECIPIENT IS THE ONE THING THIS COMPONENT MUST NOT DECIDE.
 *
 * The private conversations added on 2026-08-18 use this very box — same length
 * rules, same offline sentence, same Ctrl+Enter, same attachment picker — and
 * the only difference between a room message and a private one is the route it
 * is posted to. So the SENDER is injected rather than the peer id: this file
 * never learns there is such a thing as a recipient, and therefore cannot get
 * one wrong. `useSendDirectMessage` binds its peer when the thread mounts, so a
 * sentence typed for one person is not postable to another.
 *
 * Structurally typed on the three fields the box reads, not on
 * `UseMutationResult`, so the mutation's own generics do not leak into every
 * caller.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface MessageSender {
  mutate: (
    variables: { body: string; attachment?: MessageAttachmentInput },
    options?: { onSuccess?: () => void },
  ) => void
  isPending: boolean
  isError: boolean
  error: unknown
}

export interface MessageComposerProps {
  /** Focus target after a send, so the next sentence can be typed straight away. */
  autoFocus?: boolean
  /** Where the message goes. Defaults to the SHARED ROOM — see {@link MessageSender}. */
  sender?: MessageSender
}

export function MessageComposer({
  autoFocus = false,
  sender,
}: MessageComposerProps): React.JSX.Element {
  const { t } = useTranslation()
  const online = useIsOnline()
  /*
   * CALLED UNCONDITIONALLY — hooks cannot sit behind a branch, and this one
   * costs nothing when unused: `useMutation` registers an observer and makes no
   * request until something calls `mutate`.
   */
  const room = useSendMessage()
  const send = sender ?? room

  const [body, setBody] = React.useState("")
  const [attachment, setAttachment] = React.useState<AttachmentChoice | null>(null)
  const [picking, setPicking] = React.useState(false)

  const trimmed = body.trim()
  const tooLong = trimmed.length > MESSAGE_BODY_MAX
  const canSend = trimmed !== "" && !tooLong && online && !send.isPending

  function submit(): void {
    if (!canSend) return
    const payload: MessageAttachmentInput | undefined =
      attachment === null ? undefined : { type: attachment.type, id: attachment.id }

    send.mutate(
      { body: trimmed, ...(payload === undefined ? {} : { attachment: payload }) },
      {
        onSuccess: () => {
          // CLEARED ONLY ON SUCCESS. Clearing optimistically would lose a message
          // the server refused — and the reader would have nothing to retry with
          // but their memory of what they typed.
          setBody("")
          setAttachment(null)
          setPicking(false)
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-2 border-t p-3 md:p-4" data-testid="message-composer">
      {attachment === null ? null : (
        <div className="flex items-center gap-2 text-sm">
          <span className="bg-muted flex items-center gap-1.5 rounded-full px-2.5 py-1">
            <PaperclipIcon className="size-3.5" aria-hidden="true" />
            {t(ATTACHMENT_ABOUT_KEY[attachment.type], { number: attachment.label })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("chat.removeAttachment")}
            onClick={() => {
              setAttachment(null)
            }}
          >
            <XIcon />
          </Button>
        </div>
      )}

      {picking ? (
        <AttachmentPicker
          onPick={(choice) => {
            setAttachment(choice)
            setPicking(false)
          }}
          onCancel={() => {
            setPicking(false)
          }}
        />
      ) : null}

      <div className="flex items-end gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-label={t("chat.attach")}
          disabled={!online}
          onClick={() => {
            setPicking((open) => !open)
          }}
        >
          <PaperclipIcon />
        </Button>

        <Textarea
          value={body}
          autoFocus={autoFocus}
          disabled={!online}
          /*
           * ONE LINE UNTIL THERE IS MORE THAN ONE LINE (owner's ask,
           * 2026-08-20). It was `rows={2}` with the base `min-h-16` — 4rem, twice
           * the `h-8` of the paperclip and Send beside it — so an empty box was
           * the biggest thing on a phone screen whose point is the transcript.
           * `lib/composer-size.ts` owns both halves of the answer and explains
           * why the rows are COUNTED rather than measured.
           */
          rows={composerRows(body)}
          /*
           * `maxLength` IS NOT SET, deliberately. The browser would silently stop
           * accepting characters at 2,000 with no explanation — on a phone, mid
           * sentence, in Marathi. The counter below appears as the limit
           * approaches and the schema refuses anything over it with a message, so
           * the reader is told rather than blocked.
           */
          placeholder={online ? t("chat.placeholder") : t("chat.offlinePlaceholder")}
          aria-label={t("chat.compose")}
          onChange={(event) => {
            setBody(event.target.value)
          }}
          onKeyDown={(event) => {
            /*
             * Ctrl/⌘+Enter SENDS; plain Enter makes a new line. The other way
             * round is the desktop chat convention and it is wrong on the device
             * this is used on: a phone keyboard's return key is how you write a
             * second line, and a box that fires off half a sentence every time
             * somebody reaches for a line break is one they stop using.
             */
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
              event.preventDefault()
              submit()
            }
          }}
          className={cn("flex-1", COMPOSER_BOX_CLASS)}
          data-testid="message-input"
        />

        <Button
          disabled={!canSend}
          onClick={submit}
          aria-label={t("chat.send")}
          data-testid="message-send"
        >
          <SendHorizontalIcon />
          <span className="sr-only md:not-sr-only">{t("chat.send")}</span>
        </Button>
      </div>

      {/*
        * THE COUNTER APPEARS ONLY NEAR THE LIMIT. A character count sitting under
        * every message is a nag about a limit nobody is near — two thousand
        * characters is four hundred words.
        */}
      {trimmed.length > MESSAGE_BODY_MAX - 200 ? (
        <p className={tooLong ? "text-destructive text-xs" : "text-muted-foreground text-xs"}>
          {t("chat.charactersLeft", { count: MESSAGE_BODY_MAX - trimmed.length })}
        </p>
      ) : null}

      {online ? null : (
        <Alert data-testid="offline-notice">
          {/*
            * THE TRUE SENTENCE. Not "it will send when you are back online" —
            * there is no queue, and a promise the app cannot keep is worse than
            * a plain refusal.
            */}
          <AlertDescription>{t("chat.offline")}</AlertDescription>
        </Alert>
      )}

      {send.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            {send.error instanceof ApiError ? send.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Attaching a document                                                        */
/* -------------------------------------------------------------------------- */

interface AttachmentChoice {
  type: MessageAttachmentType
  id: number
  /** What the chip shows before it is sent. The SERVER's label wins afterwards. */
  label: string
}

/**
 * Pick the document a message is about.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT REUSES `useGlobalSearch` RATHER THAN ADDING AN ENDPOINT OR A SECOND SEARCH.
 *
 * The command palette already searches invoices, quotations, challans and credit
 * notes by number and by customer, already handles a module whose flag is off
 * (each request `.catch(() => null)`s its own 404, so a switched-off module
 * simply has no rows), and already debounces. A second search would be a second
 * answer to "which bills match 67", and the two would drift.
 *
 * The four kinds it offers are exactly `MESSAGE_ATTACHMENT_TYPES`; customers and
 * items are dropped, because a message is about a DOCUMENT — those two have no
 * number to caption and the API would refuse them anyway.
 *
 * THE CHIP'S LABEL HERE IS PROVISIONAL. What is stored is read off the document
 * server-side at send time, so a label that was somehow stale on screen cannot
 * become the caption in the record.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function AttachmentPicker({
  onPick,
  onCancel,
}: {
  onPick: (choice: AttachmentChoice) => void
  onCancel: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [query, setQuery] = React.useState("")
  const results = useGlobalSearch(query)

  const hits: AttachmentChoice[] = [
    ...results.invoices.map((row) => ({ type: "invoice" as const, id: row.id, label: row.title })),
    ...results.quotations.map((row) => ({
      type: "quotation" as const,
      id: row.id,
      label: row.title,
    })),
    ...results.challans.map((row) => ({ type: "challan" as const, id: row.id, label: row.title })),
    ...results.creditNotes.map((row) => ({
      type: "creditNote" as const,
      id: row.id,
      label: row.title,
    })),
  ]

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3" data-testid="attachment-picker">
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={query}
          placeholder={t("chat.findDocument")}
          aria-label={t("chat.findDocument")}
          onChange={(event) => {
            setQuery(event.target.value)
          }}
        />
        <Button variant="ghost" onClick={onCancel}>
          {t("btn.cancel")}
        </Button>
      </div>

      {query.trim().length < MIN_SEARCH_LENGTH ? (
        <p className="text-muted-foreground text-xs">{t("chat.findDocumentHelp")}</p>
      ) : null}

      {query.trim().length >= MIN_SEARCH_LENGTH && !results.searching && hits.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t("chat.noDocuments")}</p>
      ) : null}

      <ul className="flex flex-col">
        {hits.map((hit) => (
          <li key={`${hit.type}-${String(hit.id)}`}>
            <button
              type="button"
              className="hover:bg-muted focus-visible:ring-ring flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm focus-visible:ring-2 focus-visible:outline-none"
              onClick={() => {
                onPick(hit)
              }}
            >
              {t(ATTACHMENT_ABOUT_KEY[hit.type], { number: hit.label })}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
